import React, { Component } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { getCurrentTime } from '../../utils/getCurrentTime';
import useWebhookImportStore from '../WebhookImport/useWebhookImportStore';
import { send_request } from '../../utils/Request';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { Box, Divider, IconButton, Tooltip, Typography, alpha, styled } from '@mui/material';
import CustomPaper from '../common/CustomPaper';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { CSVLink } from 'react-csv';
import Dialog from '@mui/material/Dialog';
import config from '../../config';
import KeyIcon from '@mui/icons-material/Key';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const HeaderText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '12px',
  fontWeight: 500,
  marginBottom: '4px',
}));

const TokenButtonsContainer = styled(Grid)({
  display: 'flex',
  gap: '12px',
});

const tokenButtonSx = { minWidth: 140, height: 40 };

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: 8,
  top: 8,
  color: theme.palette.text.secondary,
}));

const StyledPaper = styled(CustomPaper)(({ theme }) => ({
  padding: '24px',
  borderRadius: '8px',
  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
  marginBottom: '24px',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    transition: 'all 0.2s',
    backgroundColor: alpha(theme.palette.action.hover, 0.7),
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px',
    },
    '&.Mui-disabled': {
      backgroundColor: alpha(theme.palette.action.hover, 0.5),
    },
  },
}));

class GenerateToken extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasToken: this.props.tokenStatus,
      loadingCircle: false,
      generateOpen: false,
      regenerateOpen: false,
      confirmRegenerateOpen: false,
      showPassword: false,
      showURL: false,
      webhookTokenURL: '',
      tokenCopied: false,
      urlCopied: false,
    };
  }

  handleRegenerateClick = () => {
    // Show confirmation dialog before regenerating
    this.setState({ confirmRegenerateOpen: true });
  };

  handleConfirmRegenerate = () => {
    this.setState({ confirmRegenerateOpen: false });
    this.generateToken();
  };

  handleCancelRegenerate = () => {
    this.setState({ confirmRegenerateOpen: false });
  };

  generateToken = async () => {
    this.setState({
      loadingCircle: true,
    });
    let lastModified = getCurrentTime();
    let dataSave = {};
    if (!this.props.data) {
      dataSave = useWebhookImportStore.getState().data;
    } else {
      dataSave = this.props.data;
    }

    let data = {
      componentData: {
        data: dataSave,
        name: this.props.name,
        lastModified: lastModified,
      },
      componentId: this.props.component_id,
      type: useWebhookImportStore.getState().getTemplate().type,
    };

    let generateTokenUrl = 'component-service/webhook/generate-token';

    if (this.props.generateTokenUrl) {
      generateTokenUrl = this.props.generateTokenUrl;
    }

    const json = await send_request(generateTokenUrl, data, '', 'POST');

    if (json.data && json.status == '200') {
      setTimeout(() => {
        this.setState({
          generateOpen: true,
          loadingCircle: false,
          token: json.data,
          hasToken: true,
        });
        this.props.hasToken(true);
      }, 1000);
    } else {
      this.setState({ loadingCircle: false });
    }
  };

  deleteToken = async () => {
    this.setState({
      deleteLoadingCircle: true,
    });
    let lastModified = getCurrentTime();

    let dataSave = {};
    if (!this.props.data) {
      dataSave = useWebhookImportStore.getState().data;
    } else {
      dataSave = this.props.data;
    }

    let data = {
      componentData: {
        data: dataSave,
        name: this.props.name,
        lastModified: lastModified,
      },
      componentId: this.props.component_id,
      type: useWebhookImportStore.getState().getTemplate().type,
    };

    let deleteTokenUrl = 'component-service/webhook/delete-token';

    if (this.props.deleteTokenUrl) {
      deleteTokenUrl = this.props.deleteTokenUrl;
    }

    const json = await send_request(deleteTokenUrl, data, '', 'POST');
    if (json && json.status == '200') {
      setTimeout(() => {
        this.setState({ deleteLoadingCircle: false, hasToken: false });
        this.props.hasToken(false);
      }, 1000);
    }
  };

  handleGenerateToken = () => {
    this.setState({
      generateOpen: false,
    });
  };

  handleClickShowPassword = () => {
    const { showPassword } = this.state;
    this.setState({
      showPassword: !showPassword,
    });
  };

  handleMouseDownPassword = event => {
    event.preventDefault();
  };

  copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(
      () => {
        if (field === 'token') {
          this.setState({ tokenCopied: true });
          setTimeout(() => this.setState({ tokenCopied: false }), 2000);
        } else if (field === 'url') {
          this.setState({ urlCopied: true });
          setTimeout(() => this.setState({ urlCopied: false }), 2000);
        }
      },
      err => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  getEndpointTokenURL = () => {
    if (!this.state.token) return <></>; // If no token, don't show anything
    const url = `${config.WEBHOOK_ENDPOINT.PRODUCTION}/${this.props.component_id}?token=${this.state.token}`;

    return (
      <Grid item xs={12}>
        <Box mb={1}>
          <HeaderText>Endpoint URL + token</HeaderText>
        </Box>
        <StyledTextField
          fullWidth
          disabled
          value={url}
          variant="outlined"
          type={this.state.showURL ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={this.state.showURL ? 'Hide URL' : 'Show URL'}>
                  <IconButton
                    aria-label="toggle URL visibility"
                    onClick={() => {
                      this.setState({ showURL: !this.state.showURL });
                    }}
                    edge="end"
                  >
                    {this.state.showURL ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={this.state.urlCopied ? 'Copied!' : 'Copy to clipboard'}>
                  <IconButton
                    aria-label="copy URL to clipboard"
                    onClick={() => this.copyToClipboard(url, 'url')}
                    edge="end"
                  >
                    {this.state.urlCopied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    );
  };

  render() {
    return (
      <>
        {/* Regenerate Confirmation Dialog */}
        <Dialog
          open={this.state.confirmRegenerateOpen}
          onClose={this.handleCancelRegenerate}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '8px',
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon sx={{ color: '#f57c00' }} />
            <Typography variant="h6" fontWeight={600}>
              Regenerate Token?
            </Typography>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2, pb: 2 }}>
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '6px' }}>
              <AlertTitle sx={{ fontWeight: 600 }}>This action has consequences</AlertTitle>
              <Typography variant="body2" component="div">
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>The current token will be <strong>permanently invalidated</strong></li>
                  <li>Any external systems using the old token will <strong>stop working immediately</strong></li>
                  <li>You must <strong>republish this workflow</strong> for the new token to work</li>
                  <li>You must <strong>update all external systems</strong> with the new token</li>
                </ul>
              </Typography>
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Are you sure you want to regenerate the token? This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={this.handleCancelRegenerate}
              variant="outlined"
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={this.handleConfirmRegenerate}
              variant="contained"
              color="warning"
              sx={{ textTransform: 'none' }}
              startIcon={<AutorenewIcon />}
            >
              Yes, Regenerate Token
            </Button>
          </DialogActions>
        </Dialog>

        {/* Token Generated Dialog */}
        <Dialog
          fullWidth={true}
          maxWidth={'sm'}
          open={this.state.generateOpen}
          onClose={() => {
            this.setState({ generateOpen: false });
          }}
          PaperProps={{
            sx: {
              borderRadius: '8px',
              overflow: 'visible',
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, pr: 6 }}>
            <Typography variant="h6" fontWeight={600}>
              Generate Token
            </Typography>
            <CloseButton
              aria-label="close"
              onClick={() => {
                this.setState({ generateOpen: false });
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </CloseButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 3, pb: 3 }}>
            <Box mb={3}>
              <Typography color="text.secondary" variant="body2" paragraph>
                The token below is required for any incoming data to be accepted by this component.
              </Typography>

            </Box>

            <Alert
              severity="warning"
              sx={{
                mb: 3,
                borderRadius: '6px',
                '& .MuiAlert-icon': {
                  alignItems: 'center',
                },
              }}
            >
              <AlertTitle sx={{ fontWeight: 600 }}>Important</AlertTitle>
              <Typography variant="body2">
                This is the <strong>only</strong> time that this token can be viewed or downloaded.
                When this window is closed, you will not be able to view or download this token ever
                again. However, you can generate a new token at any time.
              </Typography>
            </Alert>

            <StyledPaper elevation={0}>
              <Box mb={3}>
                <HeaderText>Token</HeaderText>
                <StyledTextField
                  fullWidth
                  variant="outlined"
                  disabled={true}
                  type={this.state.showPassword ? 'text' : 'password'}
                  value={this.state.token}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={this.state.showPassword ? 'Hide token' : 'Show token'}>
                          <IconButton
                            aria-label="toggle token visibility"
                            onClick={this.handleClickShowPassword}
                            onMouseDown={this.handleMouseDownPassword}
                            edge="end"
                          >
                            {this.state.showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={this.state.tokenCopied ? 'Copied!' : 'Copy to clipboard'}>
                          <IconButton
                            aria-label="copy token to clipboard"
                            onClick={() => this.copyToClipboard(this.state.token, 'token')}
                            edge="end"
                          >
                            {this.state.tokenCopied ? (
                              <CheckIcon color="success" />
                            ) : (
                              <ContentCopyIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                {!this.props.componentType && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    Include this in the incoming request as a header with the name "token" i.e. -H
                    "token: token value here" or use the endpoint URL below which contains the token
                  </Typography>
                )}
              </Box>

              {this.getEndpointTokenURL()}
            </StyledPaper>

            <Box display="flex" gap={2}>
              <CSVLink
                data={this.state.token}
                filename={this.props.component_id + '.csv'}
                style={{ textDecoration: 'none' }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  sx={tokenButtonSx}
                >
                  Download Token
                </Button>
              </CSVLink>

              <Button
                variant="contained"
                color="primary"
                onClick={this.handleGenerateToken}
                sx={{ marginLeft: 'auto' }}
              >
                Done
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        {!this.state.hasToken ? (
          <TokenButtonsContainer
            item
            container
            sx={{ justifyContent: this.props.isAlignCenter ? 'center' : 'flex-start' }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={this.generateToken}
              disabled={this.state.loadingCircle}
              startIcon={
                this.state.loadingCircle ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <KeyIcon />
                )
              }
              sx={tokenButtonSx}
            >
              Generate Token
            </Button>
          </TokenButtonsContainer>
        ) : (
          <TokenButtonsContainer
            item
            container
            sx={{ justifyContent: this.props.isAlignCenter ? 'center' : 'flex-start' }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleRegenerateClick}
              disabled={this.state.loadingCircle}
              startIcon={
                this.state.loadingCircle ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <AutorenewIcon />
                )
              }
              sx={tokenButtonSx}
            >
              Regenerate Token
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={this.deleteToken}
              disabled={this.state.deleteLoadingCircle}
              startIcon={
                this.state.deleteLoadingCircle ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              sx={tokenButtonSx}
            >
              Delete Token
            </Button>
          </TokenButtonsContainer>
        )}
      </>
    );
  }
}

export default GenerateToken;
